"use client";

import { SafeImage } from "components/ui/v2/safe-image";
import useTranslation from "next-translate/useTranslation";

export interface CardAuthorV2Props {
  firstName?: string | null;
  lastName?: string | null;
  avatarUrl?: string | null;
}

/**
 * CardAuthorV2
 * ────────────
 * Componente canónico para mostrar autor en el cuerpo de *CardV2.
 * Ubicación: parte inferior del cuerpo (flex-1), antes del footer.
 * 
 * Estructura:
 * - Avatar (18×18 px): imagen o fallback con iniciales (2 caracteres)
 * - Texto: "Por {nombre} {apellido}" con tipografía §6.12
 * 
 * Props:
 * - firstName: nombre del autor
 * - lastName: apellido del autor
 * - avatarUrl: URL de la imagen del avatar (opcional)
 * 
 * Tipografía (§6.12):
 * - Wrapper: text-[10px] text-slate-400 dark:text-zinc-500
 * - Label "Por": sin cambios
 * - Nombre+apellido: font-medium text-[10px] text-slate-500 dark:text-zinc-400
 */
export function CardAuthorV2({ firstName, lastName, avatarUrl }: CardAuthorV2Props) {
  const { t } = useTranslation("common");

  if (!firstName) {
    return null;
  }

  return (
    <div className="mt-2 flex items-center gap-2 text-[10px] text-slate-400 dark:text-zinc-500">
      {/* Avatar: 18×18 px, rounded-full */}
      {avatarUrl ? (
        <SafeImage
          src={avatarUrl}
          alt={`${firstName ?? ""} ${lastName ?? ""}`}
          width={18}
          height={18}
          className="rounded-full object-cover opacity-75"
          fallback={
            <span className="flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full bg-slate-200/70 text-[9px] font-semibold text-slate-500 dark:bg-zinc-800 dark:text-zinc-500">
              {firstName?.charAt(0)}
              {lastName?.charAt(0)}
            </span>
          }
        />
      ) : (
        /* Fallback: iniciales (2 caracteres) */
        <span className="flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full bg-slate-200/70 text-[9px] font-semibold text-slate-500 dark:bg-zinc-800 dark:text-zinc-500">
          {firstName?.charAt(0)}{lastName?.charAt(0)}
        </span>
      )}

      {/* Texto: "Por {nombre} {apellido}" */}
      <span className="min-w-0 truncate">
        {t("Por")}{" "}
        <span className="font-medium text-[10px] text-slate-500 dark:text-zinc-400">
          {firstName} {lastName}
        </span>
      </span>
    </div>
  );
}
